import React, { useState, useEffect } from 'react';
import { Shield, AlertTriangle, MessageCircle } from 'lucide-react';
import { formatDateTime } from '../../utils/locale';
import { useLanguage } from '../../i18n/LanguageContext';
import { supportApi } from '../../api/support';

const SecurityTab = ({
  reports,
  total,
  supportTickets = [],
  supportTotal = 0,
  loading,
  showModal,
  setShowModal,
  form,
  setForm,
  onSubmit,
  userId,
  onSupportTicketsRefresh
}) => {
  const [sectionType, setSectionType] = useState('security'); // security, support
  const [showSupportModal, setShowSupportModal] = useState(false);
  const [supportForm, setSupportForm] = useState({ subject: '', message: '' });
  const [supportSubmitting, setSupportSubmitting] = useState(false);
  const { t, locale } = useLanguage();
  const ITEMS_PER_PAGE = 3;
  const [securityPage, setSecurityPage] = useState(0);
  const [supportPage, setSupportPage] = useState(0);

  useEffect(() => {
    if (sectionType === 'security') {
      setSecurityPage(0);
    } else {
      setSupportPage(0);
    }
  }, [sectionType]);
  const formatDate = (dateString) => {
    if (!dateString) return t('common.notAvailable');
    return formatDateTime(dateString, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    }, locale);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-yellow-500/20 text-yellow-400';
      case 'investigating': return 'bg-blue-500/20 text-blue-400';
      case 'in_progress': return 'bg-blue-500/20 text-blue-400';
      case 'resolved': return 'bg-green-500/20 text-green-400';
      case 'dismissed': return 'bg-gray-500/20 text-gray-400';
      case 'closed': return 'bg-green-500/20 text-green-400';
      default: return 'bg-gray-500/20 text-gray-400';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical': return 'bg-red-500/20 text-red-400';
      case 'high': return 'bg-orange-500/20 text-orange-400';
      case 'medium': return 'bg-yellow-500/20 text-yellow-400';
      case 'low': return 'bg-green-500/20 text-green-400';
      default: return 'bg-gray-500/20 text-gray-400';
    }
  };

  const renderPagination = (page, setPage, totalItems) => {
    if (totalItems <= ITEMS_PER_PAGE) return null;
    const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);

    return (
      <div className="flex items-center justify-center space-x-4 mt-6">
        <button
          onClick={() => setPage(Math.max(0, page - 1))}
          disabled={page === 0}
          className={`px-4 py-2 rounded-lg font-semibold transition-all ${
            page === 0
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-yellow-400 text-black hover:bg-yellow-500'
          }`}
        >
          {t('common.previous')}
        </button>
        <span className="text-gray-400 text-sm">
          {t('common.pageOf', { page: page + 1, total: totalPages })}
        </span>
        <button
          onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
          disabled={page >= totalPages - 1}
          className={`px-4 py-2 rounded-lg font-semibold transition-all ${
            page >= totalPages - 1
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-yellow-400 text-black hover:bg-yellow-500'
          }`}
        >
          {t('common.next')}
        </button>
      </div>
    );
  };

  const paginatedSupportTickets = supportTickets.slice(
    supportPage * ITEMS_PER_PAGE,
    (supportPage + 1) * ITEMS_PER_PAGE
  );
  const paginatedReports = reports.slice(
    securityPage * ITEMS_PER_PAGE,
    (securityPage + 1) * ITEMS_PER_PAGE
  );

  const handleSupportTicketSubmit = async (e) => {
    e.preventDefault();
    if (!userId || !supportForm.message.trim()) return;
    setSupportSubmitting(true);
    try {
      const message = supportForm.message.trim();
      const subject = supportForm.subject.trim() || undefined;
      const response = await supportApi.escalate({
        userId,
        message,
        subject
      });
      if (response?.success) {
        setShowSupportModal(false);
        setSupportForm({ subject: '', message: '' });
        if (typeof onSupportTicketsRefresh === 'function') {
          await onSupportTicketsRefresh();
        }
      }
    } catch (err) {
      console.error('Support ticket error:', err);
      alert(t('security.supportTicketError'));
    } finally {
      setSupportSubmitting(false);
    }
  };

  const sectionToggleClass = (active) =>
    `px-4 py-2 rounded-lg font-semibold border transition-colors ${
      active
        ? 'bg-blue-600 text-white border-blue-600 shadow'
        : 'bg-slate-200 text-slate-800 border-slate-300 hover:bg-slate-300'
    }`;

  return (
    <div className="space-y-4">
      <div className="football-card glass-football p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold text-[#1e293b] flex items-center">
            <Shield className="w-6 h-6 mr-2" />
            {sectionType === 'support' ? t('security.titleSupport') : t('security.titleSecurity')}
          </h2>
          <div className="flex flex-wrap items-center justify-end gap-3">
            <span className="text-sm text-slate-600">
              {t('security.total', { count: sectionType === 'support' ? supportTotal : total })}
            </span>
            {sectionType === 'security' && (
              <button
                type="button"
                onClick={() => setShowModal(true)}
                className="btn-football bg-blue-600 hover:bg-blue-700"
              >
                <AlertTriangle className="w-4 h-4 mr-2 inline" />
                {t('security.reportIssue')}
              </button>
            )}
            {sectionType === 'support' && userId && (
              <button
                type="button"
                onClick={() => setShowSupportModal(true)}
                className="btn-football bg-blue-600 hover:bg-blue-700"
              >
                <MessageCircle className="w-4 h-4 mr-2 inline" />
                {t('security.openSupportTicket')}
              </button>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-4">
          <button
            type="button"
            onClick={() => setSectionType('security')}
            className={sectionToggleClass(sectionType === 'security')}
          >
            {t('security.sectionSecurity')}
          </button>
          <button
            type="button"
            onClick={() => setSectionType('support')}
            className={sectionToggleClass(sectionType === 'support')}
          >
            {t('security.sectionSupport')}
          </button>
        </div>

        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500 mx-auto"></div>
            <p className="mt-4 text-gray-400">
              {sectionType === 'support' ? t('security.loadingSupport') : t('security.loadingSecurity')}
            </p>
          </div>
        ) : (sectionType === 'support' ? supportTickets.length === 0 : reports.length === 0) ? (
          <div className="text-center py-8">
            <Shield className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-400 mb-4">
              {sectionType === 'support' ? t('security.emptySupport') : t('security.emptySecurity')}
            </p>
            {sectionType === 'security' ? (
              <button
                type="button"
                onClick={() => setShowModal(true)}
                className="btn-football bg-blue-600 hover:bg-blue-700"
              >
                {t('security.createFirst')}
              </button>
            ) : userId ? (
              <button
                type="button"
                onClick={() => setShowSupportModal(true)}
                className="btn-football bg-blue-600 hover:bg-blue-700"
              >
                <MessageCircle className="w-4 h-4 mr-2 inline" />
                {t('security.openSupportTicket')}
              </button>
            ) : null}
          </div>
        ) : (
          <>
            <div className="space-y-3">
              {sectionType === 'support'
              ? paginatedSupportTickets.map((ticket) => (
                  <div
                    key={ticket.id}
                    className="p-4 rounded-lg border-2 border-gray-500/30 bg-gray-500/5"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-white mb-1">{ticket.subject}</h3>
                        <div className="flex items-center space-x-2 mb-2">
                          <span className={`px-2 py-1 rounded text-xs font-semibold ${getStatusColor(ticket.status)}`}>
                            {ticket.status}
                          </span>
                        </div>
                        <p className="text-sm text-gray-300 mb-2">{ticket.initialMessage}</p>
                        {ticket.botReply && (
                          <div className="mt-2 p-2 bg-blue-500/10 border border-blue-500/30 rounded">
                            <p className="text-xs text-blue-300 font-semibold mb-1">{t('security.botReply')}:</p>
                            <p className="text-xs text-blue-200">{ticket.botReply}</p>
                          </div>
                        )}
                        <p className="text-xs text-gray-400">
                          {t('security.created')}: {formatDate(ticket.createdAt)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))
              : paginatedReports.map((report) => (
                  <div
                    key={report.id}
                    className="p-4 rounded-lg border-2 border-gray-500/30 bg-gray-500/5"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-white mb-1">{report.title}</h3>
                        <div className="flex items-center space-x-2 mb-2">
                          <span className={`px-2 py-1 rounded text-xs font-semibold ${getStatusColor(report.status)}`}>
                            {report.status}
                          </span>
                          <span className={`px-2 py-1 rounded text-xs font-semibold ${getPriorityColor(report.priority)}`}>
                            {report.priority}
                          </span>
                          <span className="text-xs text-gray-400">{report.reportType}</span>
                        </div>
                        <p className="text-sm text-gray-300 mb-2">{report.description}</p>
                        <p className="text-xs text-gray-400">
                          {t('security.created')}: {formatDate(report.createdAt)}
                          {report.resolvedAt && ` • ${t('security.resolved')}: ${formatDate(report.resolvedAt)}`}
                        </p>
                        {report.adminNotes && (
                          <div className="mt-2 p-2 bg-blue-500/10 border border-blue-500/30 rounded">
                            <p className="text-xs text-blue-300 font-semibold mb-1">{t('security.adminNotes')}:</p>
                            <p className="text-xs text-blue-200">{report.adminNotes}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
            </div>
            {sectionType === 'support'
              ? renderPagination(supportPage, setSupportPage, supportTickets.length)
              : renderPagination(securityPage, setSecurityPage, reports.length)}
          </>
        )}
      </div>

      {/* Support ticket modal */}
      {showSupportModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="football-card glass-football p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold text-[#1e293b] mb-4">{t('security.supportTicketModalTitle')}</h2>
            <form onSubmit={handleSupportTicketSubmit} className="space-y-4">
              <div>
                <label className="block text-gray-300 mb-2">{t('security.supportSubject')}</label>
                <input
                  type="text"
                  value={supportForm.subject}
                  onChange={(e) => setSupportForm({ ...supportForm, subject: e.target.value })}
                  className="w-full"
                  placeholder={t('security.supportSubjectPlaceholder')}
                />
              </div>
              <div>
                <label className="block text-gray-300 mb-2">{t('security.supportMessage')}</label>
                <textarea
                  value={supportForm.message}
                  onChange={(e) => setSupportForm({ ...supportForm, message: e.target.value })}
                  className="w-full"
                  rows={5}
                  placeholder={t('security.supportMessagePlaceholder')}
                  required
                />
              </div>
              <div className="flex space-x-4">
                <button
                  type="submit"
                  disabled={supportSubmitting || !supportForm.message.trim()}
                  className="btn-football flex-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {supportSubmitting ? t('common.loading') : t('security.submitSupportTicket')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowSupportModal(false);
                    setSupportForm({ subject: '', message: '' });
                  }}
                  className="px-6 py-3 border-2 border-gray-500 text-gray-300 hover:bg-gray-700 font-semibold rounded-lg transition-all"
                >
                  {t('security.cancel')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Security Report Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="football-card glass-football p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold text-[#1e293b] mb-4">{t('security.reportModalTitle')}</h2>
            <form onSubmit={onSubmit} className="space-y-4">
              <div>
                <label className="block text-gray-300 mb-2">{t('security.reportType')}</label>
                <select
                  value={form.reportType}
                  onChange={(e) => setForm({ ...form, reportType: e.target.value })}
                  className="w-full"
                  required
                >
                  <option value="account_compromise">{t('security.reportTypeOptions.accountCompromise')}</option>
                  <option value="suspicious_activity">{t('security.reportTypeOptions.suspiciousActivity')}</option>
                  <option value="phishing">{t('security.reportTypeOptions.phishing')}</option>
                  <option value="unauthorized_access">{t('security.reportTypeOptions.unauthorizedAccess')}</option>
                  <option value="other">{t('security.reportTypeOptions.other')}</option>
                </select>
              </div>

              <div>
                <label className="block text-gray-300 mb-2">{t('security.priority')}</label>
                <select
                  value={form.priority}
                  onChange={(e) => setForm({ ...form, priority: e.target.value })}
                  className="w-full"
                  required
                >
                  <option value="low">{t('security.priorityOptions.low')}</option>
                  <option value="medium">{t('security.priorityOptions.medium')}</option>
                  <option value="high">{t('security.priorityOptions.high')}</option>
                  <option value="critical">{t('security.priorityOptions.critical')}</option>
                </select>
              </div>

              <div>
                <label className="block text-gray-300 mb-2">{t('security.title')}</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="w-full"
                  placeholder={t('security.titlePlaceholder')}
                  required
                />
              </div>

              <div>
                <label className="block text-gray-300 mb-2">{t('security.description')}</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="w-full"
                  rows={5}
                  placeholder={t('security.descriptionPlaceholder')}
                  required
                />
              </div>

              <div className="flex space-x-4">
                <button
                  type="submit"
                  className="btn-football flex-1 bg-blue-600 hover:bg-blue-700"
                >
                  {t('security.submit')}
                </button>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-6 py-3 border-2 border-gray-500 text-gray-300 hover:bg-gray-700 font-semibold rounded-lg transition-all"
                >
                  {t('security.cancel')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SecurityTab;
